import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const IndicesEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const curriculum = [
    {
      title: 'Understanding Stock Indices',
      lessons: [
        'What are stock indices and how they work',
        'Market-cap weighted vs. price-weighted indices',
        'Index components and rebalancing',
        'Cash indices vs. futures contracts',
        'How to trade indices with CFDs',
        'Benefits of index trading vs. individual stocks'
      ]
    },
    {
      title: 'Major Global Indices',
      lessons: [
        'S&P 500: US large-cap benchmark',
        'Dow Jones 30: Blue-chip industrial average',
        'NASDAQ 100: Technology-heavy index',
        'FTSE 100: UK\'s leading index',
        'DAX 40: German stock index',
        'Nikkei 225, Hang Seng, and Asian indices'
      ]
    },
    {
      title: 'Index Analysis Techniques',
      lessons: [
        'Technical analysis for indices',
        'Support and resistance levels in indices',
        'Moving averages and trend identification',
        'Volume analysis and market breadth',
        'Sector rotation within indices',
        'Using index futures for market sentiment'
      ]
    },
    {
      title: 'Fundamental Factors',
      lessons: [
        'Economic indicators affecting indices',
        'Corporate earnings seasons and their impact',
        'Central bank policies and index movements',
        'Interest rates and stock market correlation',
        'GDP growth and index performance',
        'Inflation data and market reactions'
      ]
    },
    {
      title: 'Index Trading Strategies',
      lessons: [
        'Trend following strategies for indices',
        'Range trading in sideways markets',
        'Breakout strategies at key levels',
        'News trading around major announcements',
        'Hedging portfolios with index positions',
        'Risk management for index trading'
      ]
    }
  ];

  const majorIndices = [
    { name: 'S&P 500', region: 'USA', companies: '500', description: 'Broad US market benchmark' },
    { name: 'NASDAQ 100', region: 'USA', companies: '100', description: 'Technology-focused index' },
    { name: 'Dow Jones 30', region: 'USA', companies: '30', description: 'Blue-chip industrial stocks' },
    { name: 'FTSE 100', region: 'UK', companies: '100', description: 'Leading UK companies' },
    { name: 'DAX 40', region: 'Germany', companies: '40', description: 'Top German stocks' },
    { name: 'Nikkei 225', region: 'Japan', companies: '225', description: 'Tokyo Stock Exchange' }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Stock Indices Trading Education</h1>
          <p>Learn to trade major global indices and understand market correlation</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Why Trade Stock Indices?</h2>
          <p>
            Trading stock indices offers exposure to entire markets or sectors with a single position. Instead of picking 
            individual stocks, you can trade the overall direction of the economy, reducing company-specific risk while 
            still benefiting from market movements.
          </p>
          <div className="course-stats">
            <div className="stat">
              <h3>Diversification</h3>
              <p>Multiple Stocks</p>
            </div>
            <div className="stat">
              <h3>Lower Risk</h3>
              <p>No Single Stock Risk</p>
            </div>
            <div className="stat">
              <h3>High Liquidity</h3>
              <p>Easy Entry/Exit</p>
            </div>
            <div className="stat">
              <h3>24hr Trading</h3>
              <p>Extended Hours CFDs</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Index Trading Curriculum</h2>
          <div className="modules-list">
            {curriculum.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex}>{lesson}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="indices-overview" variants={itemVariants}>
          <h2>Major Indices to Trade</h2>
          <div className="indices-grid">
            {majorIndices.map((index, i) => (
              <motion.div 
                key={i} 
                className="index-info-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <h3>{index.name}</h3>
                <div className="index-details">
                  <p><strong>Region:</strong> {index.region}</p>
                  <p><strong>Companies:</strong> {index.companies}</p>
                </div>
                <p className="index-desc">{index.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>What Moves Indices?</h2>
          <div className="factors-grid">
            <div className="factor">
              <h4>📊 Corporate Earnings</h4>
              <p>Quarterly earnings from major index components drive price action</p>
            </div>
            <div className="factor">
              <h4>🏦 Central Bank Decisions</h4>
              <p>Interest rate changes and monetary policy announcements</p>
            </div>
            <div className="factor">
              <h4>📈 Economic Data</h4>
              <p>GDP, employment, manufacturing PMI, and consumer confidence</p>
            </div>
            <div className="factor">
              <h4>🌍 Geopolitical Events</h4>
              <p>Elections, trade wars, and international tensions</p>
            </div>
            <div className="factor">
              <h4>💹 Sector Performance</h4>
              <p>Rotation between sectors affects index composition</p>
            </div>
            <div className="factor">
              <h4>🛢️ Commodity Prices</h4>
              <p>Oil and other commodities impact energy and material stocks</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-hours" variants={itemVariants}>
          <h2>Index Trading Hours</h2>
          <div className="hours-grid">
            <div className="hours-card">
              <h4 style={{color: '#06b6d4'}}>🇺🇸 US Indices (S&P 500, NASDAQ, Dow)</h4>
              <p><strong>Regular:</strong> 9:30 AM - 4:00 PM ET</p>
              <p><strong>Extended (CFD):</strong> Nearly 24 hours</p>
            </div>
            <div className="hours-card">
              <h4 style={{color: '#06b6d4'}}>🇬🇧 UK Indices (FTSE 100)</h4>
              <p><strong>Regular:</strong> 8:00 AM - 4:30 PM GMT</p>
              <p><strong>Extended (CFD):</strong> Nearly 24 hours</p>
            </div>
            <div className="hours-card">
              <h4 style={{color: '#06b6d4'}}>🇩🇪 German Index (DAX 40)</h4>
              <p><strong>Regular:</strong> 9:00 AM - 5:30 PM CET</p>
              <p><strong>Extended (CFD):</strong> Nearly 24 hours</p>
            </div>
            <div className="hours-card">
              <h4 style={{color: '#06b6d4'}}>🇯🇵 Japanese Index (Nikkei 225)</h4>
              <p><strong>Regular:</strong> 9:00 AM - 3:00 PM JST</p>
              <p><strong>Extended (CFD):</strong> Nearly 24 hours</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Index Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📈 Trend Following</h3>
              <p>Ride established trends using moving averages and trend indicators</p>
            </div>
            <div className="strategy">
              <h3>📊 Range Trading</h3>
              <p>Buy at support, sell at resistance in sideways markets</p>
            </div>
            <div className="strategy">
              <h3>💥 Breakout Trading</h3>
              <p>Enter when price breaks key levels with volume confirmation</p>
            </div>
            <div className="strategy">
              <h3>📰 News Trading</h3>
              <p>Trade volatility around economic data and Fed announcements</p>
            </div>
            <div className="strategy">
              <h3>🔄 Mean Reversion</h3>
              <p>Trade overbought/oversold conditions using RSI and Stochastics</p>
            </div>
            <div className="strategy">
              <h3>⏰ Opening Range</h3>
              <p>Trade the first hour's range breakout or rejection</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="practical-exercises" variants={itemVariants}>
          <h2>Hands-On Learning</h2>
          <div className="exercises-list">
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Chart Pattern Recognition</h4>
              <p>Identify and trade chart patterns on S&P 500 and NASDAQ</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Economic Calendar Trading</h4>
              <p>Practice trading around NFP, FOMC, and GDP releases</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Correlation Analysis</h4>
              <p>Study correlations between different global indices</p>
            </div>
            <div className="exercise">
              <h4 style={{color: '#06b6d4'}}>✓ Demo Trading Practice</h4>
              <p>Test strategies on demo account before going live</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="success-tips" variants={itemVariants}>
          <h2>Index Trading Success Tips</h2>
          <ul className="tips-list">
            <li>Focus on the most liquid indices (S&P 500, NASDAQ, DAX)</li>
            <li>Trade during regular market hours for best liquidity</li>
            <li>Monitor major index component earnings (Apple, Microsoft, etc.)</li>
            <li>Use proper position sizing - indices can move quickly</li>
            <li>Pay attention to opening gaps and their implications</li>
            <li>Understand sector weightings in the index you're trading</li>
            <li>Watch for divergences between indices for trading signals</li>
            <li>Avoid holding positions through major central bank meetings</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Master Index Trading</h2>
          <p>Learn professional index trading strategies and get real-time signals</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Indices Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Index Signals</Link>
            <Link to="/markets/indices" className="btn secondary">Explore Global Indices</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default IndicesEducation;
